/**
 * Admin: User Management page.
 *
 * List all users, create new users, edit roles, reset passwords,
 * activate/deactivate, and delete users.
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient, ApiError } from '@/services/apiClient'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  Button,
  Input,
  Badge,
  Alert,
  AlertDescription,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@/components/ui'
import {
  UserPlus,
  RotateCcw,
  Trash2,
  ShieldCheck,
  UserCheck,
  UserX,
  Copy,
  Check,
  Key,
  X,
  Eye,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { startViewAs } from '@/services/impersonation'
import { refusalText } from '@/components/auth/impersonationText'
import { UserCredentialsModal } from './UserCredentialsModal'

interface AdminUser {
  id: string
  email: string
  display_name: string
  role: string
  is_active: boolean
  must_change_password: boolean
  created_at: string | null
  updated_at: string | null
  last_login_at: string | null
}

interface RoleSummary {
  id: string
  name: string
  display_name: string
  is_system: boolean
}

interface UserRoleAssignment {
  user_id: string
  built_in_role: string
  custom_roles: string[]
}

const BUILT_IN_ROLES = ['viewer', 'user', 'manager', 'admin'] as const

export function UserManagementPage() {
  const currentUser = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  // SA-906: strict on both stores — never the assume-enabled default, or the
  // button would flash on a deployment that never enabled the flag. Two
  // separate hook calls: `a && useB()` would skip a hook and break the order.
  const viewAsFlagOn = useFeatureStore((s) => s.isLoaded && s.features.has('impersonation'))
  const viewAsPermitted = usePermissionStore((s) => s.isLoaded && s.permissions.has('users:impersonate'))
  const canViewAs = viewAsFlagOn && viewAsPermitted
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create user dialog
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '',
    display_name: '',
    password: '',
    role: 'user',
  })
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null)
  const [resetPassword, setResetPassword] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [copiedPassword, setCopiedPassword] = useState(false)

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Action loading states
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})

  // Credentials modal
  const [credentialsTarget, setCredentialsTarget] = useState<AdminUser | null>(null)

  // Role management dialog
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null)
  const [availableRoles, setAvailableRoles] = useState<RoleSummary[]>([])
  const [roleForm, setRoleForm] = useState<{ built_in_role: string; custom_role_ids: string[] }>({
    built_in_role: 'user',
    custom_role_ids: [],
  })
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [isSavingRoles, setIsSavingRoles] = useState(false)
  const [roleError, setRoleError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/admin/users')
      if (!res.ok) throw new Error('Failed to fetch users')
      const data = await res.json()
      setUsers(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // ── Create user ──

  const viewAs = async (user: AdminUser) => {
    setError(null)
    setActionLoading((l) => ({ ...l, [`viewas-${user.id}`]: true }))
    try {
      await startViewAs(user.id)
      navigate('/')
    } catch (err) {
      const code = err instanceof ApiError && err.detail && typeof err.detail === 'object'
        ? (err.detail as { code?: string }).code
        : undefined
      setError(refusalText(code))
    } finally {
      setActionLoading((l) => ({ ...l, [`viewas-${user.id}`]: false }))
    }
  }

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setIsCreating(true)
    setCreateError(null)

    try {
      const res = await apiClient.fetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(createForm),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed to create user' }))
        throw new Error(data.detail || 'Failed to create user')
      }
      setShowCreate(false)
      setCreateForm({ email: '', display_name: '', password: '', role: 'user' })
      await fetchUsers()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create user')
    } finally {
      setIsCreating(false)
    }
  }

  // ── Manage roles ──

  const openRoleManager = async (user: AdminUser) => {
    setRoleTarget(user)
    setRoleError(null)
    setIsLoadingRoles(true)

    try {
      // Fetch available roles and user's current assignments in parallel
      const [rolesRes, assignmentRes] = await Promise.all([
        apiClient.fetch('/api/admin/roles'),
        apiClient.fetch(`/api/admin/users/${user.id}/roles`),
      ])

      // Parse the roles list once and keep it in a local. The response body can
      // only be consumed once (re-reading it via `.clone()` after `.json()`
      // throws "Response body is already used"), and `availableRoles` state is
      // stale within this closure on the first open — so reuse `rolesData`
      // directly for the assignment mapping below.
      let rolesData: RoleSummary[] = availableRoles
      if (rolesRes.ok) {
        rolesData = await rolesRes.json()
        setAvailableRoles(rolesData)
      }

      if (assignmentRes.ok) {
        const assignment: UserRoleAssignment = await assignmentRes.json()
        // Find custom role IDs by matching names to available roles
        const customRoleIds = assignment.custom_roles
          .map((name) => rolesData.find((r) => r.name === name && !r.is_system)?.id)
          .filter((id): id is string => !!id)

        setRoleForm({
          built_in_role: assignment.built_in_role,
          custom_role_ids: customRoleIds,
        })
      }
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : 'Failed to load role data')
    } finally {
      setIsLoadingRoles(false)
    }
  }

  const handleSaveRoles = async () => {
    if (!roleTarget) return
    setIsSavingRoles(true)
    setRoleError(null)

    try {
      const res = await apiClient.fetch(`/api/admin/users/${roleTarget.id}/roles`, {
        method: 'PUT',
        body: JSON.stringify(roleForm),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed to update roles' }))
        throw new Error(data.detail || 'Failed to update roles')
      }
      setRoleTarget(null)
      await fetchUsers()
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : 'Failed to update roles')
    } finally {
      setIsSavingRoles(false)
    }
  }

  const toggleCustomRole = (roleId: string) => {
    setRoleForm((prev) => ({
      ...prev,
      custom_role_ids: prev.custom_role_ids.includes(roleId)
        ? prev.custom_role_ids.filter((id) => id !== roleId)
        : [...prev.custom_role_ids, roleId],
    }))
  }

  // ── Toggle active ──

  const toggleActive = async (user: AdminUser) => {
    setActionLoading((prev) => ({ ...prev, [`active-${user.id}`]: true }))
    try {
      const res = await apiClient.fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !user.is_active }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail)
      }
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setActionLoading((prev) => ({ ...prev, [`active-${user.id}`]: false }))
    }
  }

  // ── Reset password ──

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault()
    if (!resetTarget) return
    setIsResetting(true)

    try {
      const res = await apiClient.fetch(`/api/admin/users/${resetTarget.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: resetPassword }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail)
      }
      setResetSuccess(true)
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
      setResetTarget(null)
      setResetPassword('')
    } finally {
      setIsResetting(false)
    }
  }

  const closeResetDialog = () => {
    setResetTarget(null)
    setResetPassword('')
    setResetSuccess(false)
    setCopiedPassword(false)
  }

  // ── Delete user ──

  const handleDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      const res = await apiClient.fetch(`/api/admin/users/${deleteTarget.id}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail)
      }
      setDeleteTarget(null)
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setIsDeleting(false)
    }
  }

  // ── Copy to clipboard helper ──

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedPassword(true)
    setTimeout(() => setCopiedPassword(false), 2000)
  }

  // ── Format date helper ──

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="User Management"
        subtitle={`${users.length} user${users.length !== 1 ? 's' : ''} total`}
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Users table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">User</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Role</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Last Login</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => {
                const isSelf = user.id === currentUser?.id
                return (
                  <tr key={user.id} className="hover:bg-muted">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">
                        {user.display_name}
                        {isSelf && (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs">{user.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={user.role === 'admin' ? 'warning' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {!user.is_active ? (
                        <Badge variant="destructive">Inactive</Badge>
                      ) : user.must_change_password ? (
                        <Badge variant="warning">Password Reset</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatDate(user.last_login_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canViewAs && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View as"
                            disabled={isSelf || user.role === 'admin' || !user.is_active || actionLoading[`viewas-${user.id}`]}
                            onClick={() => viewAs(user)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Manage credentials */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Manage API keys"
                          onClick={() => setCredentialsTarget(user)}
                        >
                          <Key className="w-4 h-4" />
                        </Button>

                        {/* Manage roles */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Manage roles"
                          onClick={() => openRoleManager(user)}
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </Button>

                        {/* Toggle active */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                          disabled={isSelf || actionLoading[`active-${user.id}`]}
                          onClick={() => toggleActive(user)}
                        >
                          {actionLoading[`active-${user.id}`] ? (
                            <Spinner size="sm" />
                          ) : user.is_active ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </Button>

                        {/* Reset password */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Reset password"
                          onClick={() => setResetTarget(user)}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>

                        {/* Delete */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete user"
                          disabled={isSelf}
                          onClick={() => setDeleteTarget(user)}
                          className="text-destructive hover:text-destructive/90"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create User Modal ── */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="sm">
          <form onSubmit={handleCreate}>
            <ModalHeader>
              <ModalTitle>Create New User</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {createError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  placeholder="user@example.com"
                />
                <Input
                  label="Display Name"
                  value={createForm.display_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, display_name: e.target.value }))}
                  required
                  placeholder="John Doe"
                />
                <Input
                  label="Temporary Password"
                  type="text"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  required
                  placeholder="Temp password (user must change)"
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Built-in Role
                  </label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                  >
                    {BUILT_IN_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isCreating}>
                Create User
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* ── Reset Password Modal ── */}
      <Modal open={!!resetTarget} onOpenChange={(open) => !open && closeResetDialog()}>
        <ModalContent size="sm">
          {resetSuccess ? (
            <>
              <ModalHeader>
                <ModalTitle>Password Reset</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Alert variant="success" className="mb-4">
                  <AlertDescription>
                    Password has been reset for <strong>{resetTarget?.display_name}</strong>.
                    They will be required to change it on next login.
                  </AlertDescription>
                </Alert>
                <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                  <code className="flex-1 text-sm">{resetPassword}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(resetPassword)}
                    title="Copy password"
                  >
                    {copiedPassword ? (
                      <Check className="w-4 h-4 text-success" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button onClick={closeResetDialog}>Done</Button>
              </ModalFooter>
            </>
          ) : (
            <form onSubmit={handleResetPassword}>
              <ModalHeader>
                <ModalTitle>Reset Password</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-foreground mb-4">
                  Set a new temporary password for <strong>{resetTarget?.display_name}</strong> ({resetTarget?.email}).
                  They will be required to change it on next login.
                </p>
                <Input
                  label="New Temporary Password"
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  required
                  placeholder="Enter temporary password"
                  autoFocus
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" type="button" onClick={closeResetDialog}>
                  Cancel
                </Button>
                <Button type="submit" isLoading={isResetting}>
                  Reset Password
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Delete User</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Alert variant="destructive">
              <AlertDescription>
                <div>
                  <p className="font-medium">This action cannot be undone.</p>
                  <p className="mt-1">
                    Deleting <strong>{deleteTarget?.display_name}</strong> ({deleteTarget?.email})
                    will permanently remove their account and all associated data including
                    conversations, personas, credentials, and settings.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Delete User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── User Credentials Modal ── */}
      {credentialsTarget && (
        <UserCredentialsModal
          userId={credentialsTarget.id}
          userName={credentialsTarget.display_name}
          userEmail={credentialsTarget.email}
          open={true}
          onOpenChange={(open) => !open && setCredentialsTarget(null)}
        />
      )}

      {/* ── Role Management Modal ── */}
      <Modal open={!!roleTarget} onOpenChange={(open) => !open && setRoleTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Manage Roles</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {isLoadingRoles ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                {roleError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{roleError}</AlertDescription>
                  </Alert>
                )}
                <p className="text-sm text-foreground mb-4">
                  Assign roles for <strong>{roleTarget?.display_name}</strong>
                </p>

                {/* Built-in role */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Built-in Role
                  </label>
                  <select
                    value={roleForm.built_in_role}
                    onChange={(e) =>
                      setRoleForm((f) => ({ ...f, built_in_role: e.target.value }))
                    }
                    disabled={roleTarget?.id === currentUser?.id && roleForm.built_in_role === 'admin'}
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring disabled:opacity-60"
                  >
                    {BUILT_IN_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </select>
                  {roleTarget?.id === currentUser?.id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      You cannot change your own built-in role.
                    </p>
                  )}
                </div>

                {/* Custom roles */}
                {(() => {
                  const customRoles = availableRoles.filter((r) => !r.is_system)
                  if (customRoles.length === 0) return null
                  return (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Additional Roles
                      </label>

                      {/* Selected custom roles */}
                      {roleForm.custom_role_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {roleForm.custom_role_ids.map((id) => {
                            const role = customRoles.find((r) => r.id === id)
                            if (!role) return null
                            return (
                              <Badge key={id} variant="default" className="flex items-center gap-1 pr-1">
                                {role.display_name}
                                <button
                                  type="button"
                                  onClick={() => toggleCustomRole(id)}
                                  className="hover:bg-primary/20 rounded-full p-0.5"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            )
                          })}
                        </div>
                      )}

                      {/* Available custom roles */}
                      <div className="border border-border rounded-md max-h-40 overflow-y-auto">
                        {customRoles.map((role) => {
                          const isSelected = roleForm.custom_role_ids.includes(role.id)
                          return (
                            <label
                              key={role.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCustomRole(role.id)}
                                className="rounded-sm text-primary focus:ring-ring"
                              />
                              <span className="text-sm text-foreground">
                                {role.display_name}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setRoleTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRoles}
              isLoading={isSavingRoles}
              disabled={isLoadingRoles}
            >
              Save Roles
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
